import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useShellTitle } from '../components/layout/ShellTitleContext';
import { CheckInCard } from '../features/attendance';
import { LiveActivity } from '../features/classroom';
import { ClassPageStatusBanner } from '../features/classroom/components/ClassPageStatusBanner';
import { useClassroomStore } from '../features/classroom/store';
import { FeedbackBox, SurveyLinks } from '../features/surveys';
import { LearnerPlanCard } from '../features/learnerPlan';

// /class — the student-facing "what's happening in class right now" page (BP
// 22). Composition only: every card below owns its own data-fetching (each
// reads the current class session / open windows / plan state independently),
// so this file has nothing to coordinate between them. Do not lift state up
// into this component; add a prop to the relevant feature instead.
//
// 2026-09-08: the instructor's first real class exposed a gap in that
// contract — CheckInCard and LiveActivity both render nothing outside a live
// window/activity, which is most of the time, so the page looked broken
// rather than idle. ClassPageStatusBanner explains whatever state class is in
// so the page is never an empty shell.
//
// 2026-09-10, the instructor again: "shouldn't student see active class
// focused data when class is running? it shows surveys, quiz and etc while
// they are not relevant to active class." Right — during a live class this
// page is what a student looks at while the instructor is talking, and a
// quiz link is an invitation to stop paying attention. So the page now has
// two shapes, chosen by whether a class is actually live:
//
//   live      → attendance and the live activity, and NOTHING else expanded.
//               The coursework cards collapse into one disclosure, still one
//               click away, because a student who genuinely needs their
//               learner plan mid-class should not have to leave the page.
//   not live  → the full page, exactly as before.
//
// This reads the SHARED live poll (features/classroom/store) that LiveActivity
// already drives — it is ref-counted, so reading it here adds no request and
// no second interval. That is the only reason this component is allowed to
// know about it at all.
export default function ClassPage() {
  useShellTitle('Class');

  const liveSession = useClassroomStore((s) => s.session);
  const isLive = !!liveSession;
  const [showCourseWork, setShowCourseWork] = useState(false);

  const courseWork = (
    <>
      <LearnerPlanCard />
      <div className="grid gap-4 sm:grid-cols-2">
        <SurveyLinks />
        <FeedbackBox />
      </div>
    </>
  );

  return (
    <div className="ws-page space-y-4">
      {/* 2026-09-11 — while class is LIVE the page title is chrome: the app
          shell's top bar already reads "Class" (useShellTitle above), and on
          a 390 px phone these two lines were roughly a fifth of the screen
          standing between a student and the question they are being asked.
          The heading stays in the accessibility tree either way — it is the
          page's <h1> — it just stops taking pixels during a live class. */}
      <header className={isLive ? 'sr-only' : undefined}>
        <h1 className="text-xl font-semibold text-ws-ink">Class</h1>
        <p className="mt-1 text-sm text-ws-ink-3">
          {isLive
            ? 'Class is running. Everything below is for right now.'
            : 'Attendance, the live activity, and anything open for this meeting.'}
        </p>
      </header>

      <ClassPageStatusBanner />
      <CheckInCard />
      <LiveActivity />

      {isLive ? (
        <div className="space-y-4">
          <button
            onClick={() => setShowCourseWork((v) => !v)}
            className="flex w-full items-center justify-between gap-3 rounded-lg border border-ws-border bg-ws-surface px-4 py-3 text-left transition-colors hover:bg-ws-surface-2"
            aria-expanded={showCourseWork}
          >
            <span>
              <span className="text-sm font-medium text-ws-ink">Course work</span>
              <span className="ml-2 text-xs text-ws-ink-3">
                Learner plan, surveys and feedback — not part of today&rsquo;s class
              </span>
            </span>
            <ChevronDown
              size={16}
              className={`shrink-0 text-ws-ink-3 transition-transform ${showCourseWork ? 'rotate-180' : ''}`}
            />
          </button>
          {/* Unmounted while collapsed, not merely hidden: these cards each
              fetch their own data, and a live class is exactly when the page
              should not be making requests nobody asked for. */}
          {showCourseWork && courseWork}
        </div>
      ) : (
        courseWork
      )}
    </div>
  );
}
